/**
 * EEMENTOR battery pack builder.
 * Pick a cell + S/P configuration; renders the pack in 3D (Three.js, lazy-loaded) and
 * calculates pack specs. Units in the scene are millimetres.
 */

const CELL_FORMATS = {
  18650: { d: 18.5, h: 65 },
  21700: { d: 21.2, h: 70 },
};
const GAP = 0.6; // mm between neighbouring cells
const POUCH_GAP = 1; // mm between stacked pouch cells (foam / spacer)
const POUCH_ROW_GAP = 4; // mm between side-by-side pouch stacks
const POUCH_TOP = 16; // mm above the pouch body: sealed edge, tabs and busbars
const FORMAT_LABEL = { pouch: 'LiPo pouch' };
const formatLabel = (format) => FORMAT_LABEL[format] || format;
const nominalFor = (format) => (format === 'pouch' ? 3.7 : 3.6);
const OVERHEAD = 1.1; // weight allowance for strips, BMS and wrap

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Arrange S parallel groups of P cells in a near-rectangular block.
 * Each group is a k × rowsPer block; groups snake through a gx × gy grid so that
 * consecutive (series-connected) groups are always neighbours.
 */
function computeLayout(S, P) {
  let best = null;
  for (let k = 1; k <= P; k++) {
    const rowsPer = Math.ceil(P / k);
    if (k > 1 && Math.ceil(P / (k - 1)) === rowsPer) continue;
    for (let gy = 1; gy <= S; gy++) {
      const gx = Math.ceil(S / gy);
      if (gy > 1 && Math.ceil(S / (gy - 1)) === gx) continue;
      const cols = gx * k;
      const rows = gy * rowsPer;
      const waste = (gx * gy - S) / S + (k * rowsPer - P) / P;
      const score = Math.abs(Math.log(cols / rows / 1.5)) + waste * 1.5;
      if (!best || score < best.score) best = { k, rowsPer, gx, gy, cols, rows, score };
    }
  }
  return best;
}

function buildCells(S, P, pitch) {
  const L = computeLayout(S, P);
  const cells = [];
  for (let g = 0; g < S; g++) {
    const r = Math.floor(g / L.gx);
    let c = g % L.gx;
    if (r % 2) c = L.gx - 1 - c;
    for (let j = 0; j < P; j++) {
      const col = c * L.k + (j % L.k);
      const row = r * L.rowsPer + Math.floor(j / L.k);
      cells.push({ g, col, row, x: (col - (L.cols - 1) / 2) * pitch, z: (row - (L.rows - 1) / 2) * pitch });
    }
  }
  return { layout: L, cells };
}

/**
 * Pouch cells stand on their long edge and are stacked face to face along X.
 * Each parallel group is P cells in a row; groups snake through gx × gy stacks so that
 * consecutive (series-connected) groups are neighbours.
 */
function buildPouch(S, P, cell) {
  const px = cell.t + POUCH_GAP;
  const pz = cell.w + POUCH_ROW_GAP;
  let best = null;
  for (let gy = 1; gy <= S; gy++) {
    const gx = Math.ceil(S / gy);
    if (gy > 1 && Math.ceil(S / (gy - 1)) === gx) continue;
    const X = gx * P * px;
    const Z = gy * pz;
    const waste = (gx * gy - S) / S;
    const score = Math.abs(Math.log(X / Z / 1.3)) + waste * 1.5;
    if (!best || score < best.score) best = { gx, gy, score };
  }
  const { gx, gy } = best;
  const cols = gx * P;
  const cells = [];
  for (let g = 0; g < S; g++) {
    const r = Math.floor(g / gx);
    let c = g % gx;
    if (r % 2) c = gx - 1 - c;
    for (let j = 0; j < P; j++) {
      const col = c * P + j;
      cells.push({ g, col, row: r, x: (col - (cols - 1) / 2) * px, z: (r - (gy - 1) / 2) * pz });
    }
  }
  return {
    layout: { gx, gy, cols, rows: gy },
    cells,
    size: {
      length: Math.round(cols * cell.t + (cols - 1) * POUCH_GAP),
      width: Math.round(gy * cell.w + (gy - 1) * POUCH_ROW_GAP),
      height: Math.round(cell.l + POUCH_TOP),
    },
  };
}

/** Highest allowed value for a slider (its own limit, which may be below the shared track max) */
const limitOf = (input) => +(input.dataset.limit || input.max);
const clampToLimit = (input, n) => Math.min(limitOf(input), Math.max(+input.min, n));

/**
 * "Describe your pack": turn a sentence into requirements.
 * e.g. "48V e-bike battery, 20Ah, 30A continuous" → { voltage: 48, ah: 20, current: 30 }
 * When an AI endpoint is configured it returns the same shape; this parser is the fallback.
 */
function parseRequest(text, cells) {
  const t = ` ${text.toLowerCase().replace(/,/g, ' ')} `;
  const num = (re) => {
    const m = t.match(re);
    return m ? parseFloat(m[1]) : null;
  };
  const kwh = num(/(\d+(?:\.\d+)?)\s*kwh\b/);
  const kw = num(/(\d+(?:\.\d+)?)\s*kw\b/);
  const req = {
    voltage: num(/(\d+(?:\.\d+)?)\s*v(?:olts?)?\b/),
    ah: num(/(\d+(?:\.\d+)?)\s*ah\b/),
    wh: kwh ? kwh * 1000 : num(/(\d+(?:\.\d+)?)\s*wh\b/),
    current: num(/(\d+(?:\.\d+)?)\s*a(?:mps?)?\b/),
    watts: kw ? kw * 1000 : num(/(\d+(?:\.\d+)?)\s*w\b/),
    hours: num(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)\b/),
    format: t.match(/\b(18650|21700)\b/)?.[1] || (/\b(lipo|li-po|pouch|polymer)\b/.test(t) ? 'pouch' : null),
    cell: cells.find((c) => t.includes(c.name.toLowerCase()))?.name || null,
    brand: t.match(/\b(samsung|lg|molicel|murata|sony|panasonic|sanyo|eve)\b/)?.[1] || null,
    priority: null,
  };
  if (/\b(high power|high drain|power tool|drone|burst|racing|high current|peak)\b/.test(t)) req.priority = 'power';
  else if (/\b(light|lightweight|weight|compact|small|portable)\b/.test(t)) req.priority = 'light';
  else if (/\b(long runtime|range|endurance|longest|max capacity)\b/.test(t)) req.priority = 'capacity';
  return req;
}

/**
 * Pick S, P and a cell that meet the requirements. Deterministic: the numbers always come from
 * the cell data, never from a model. Returns { error } or { S, best, alts, need }.
 */
function solveDesign(req, cells, maxS, maxP) {
  if (!req.voltage) return { error: 'voltage' };
  const perCell = nominalFor(req.format);
  const S = Math.max(1, Math.round(req.voltage / perCell));
  if (S > maxS) return { error: 'too-high', S };
  const nominal = S * perCell;

  let current = req.current || (req.watts ? req.watts / nominal : null);
  let ah = req.ah || (req.wh ? req.wh / nominal : null);
  let runtimeNote = null;
  if (!ah && req.hours && current) {
    ah = req.hours * current * 1.1; // 10% margin so the pack is not drained to empty
    runtimeNote = `${req.hours} h × ${round(current)} A + 10% margin`;
  }
  if (!ah && !current) return { error: 'capacity', S };

  // Pouch cells are only suggested when the request asks for LiPo / pouch
  const formatCells = cells.filter((c) => (req.format ? c.format === req.format : c.format !== 'pouch'));
  const pool = formatCells.filter(
    (c) =>
      (!req.brand || c.name.toLowerCase().startsWith(req.brand)) &&
      (!req.cell || c.name === req.cell)
  );
  const options = [];
  for (const c of pool.length ? pool : formatCells) {
    const pForAh = ah ? Math.ceil(ah / (c.capacity / 1000)) : 1;
    const pForA = current ? Math.ceil(current / c.current) : 1;
    const P = Math.max(pForAh, pForA, 1);
    if (P > maxP) continue;
    options.push({ cell: c, P, ah: (P * c.capacity) / 1000, amps: P * c.current, kg: (S * P * c.weight * OVERHEAD) / 1000 });
  }
  if (!options.length) return { error: 'too-big', S };

  const by = {
    light: (a, b) => a.kg - b.kg,
    power: (a, b) => b.amps - a.amps || a.kg - b.kg,
    capacity: (a, b) => b.ah - a.ah || a.kg - b.kg,
  };
  const best = [...options].sort(by[req.priority] || by.light)[0];
  const alts = [];
  // Alternatives stay within ~30% of the suggestion's weight so they remain sensible trade-offs
  const near = options.filter((o) => o.kg <= best.kg * 1.3);
  for (const [label, sorter] of [['Lighter', by.light], ['More power', by.power], ['More capacity', by.capacity]]) {
    const pick = [...(label === 'Lighter' ? options : near)].sort(sorter)[0];
    if (!pick) continue;
    if (pick.cell !== best.cell && !alts.some((a) => a.cell === pick.cell)) alts.push({ ...pick, label });
  }
  return { S, nominal, best, alts: alts.slice(0, 2), need: { ah, current, runtimeNote } };
}

const round = (n) => Math.round(n * 10) / 10;
const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

const fmt = (n, digits = 1) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

class EePackBuilder extends HTMLElement {
  connectedCallback() {
    this.cells = JSON.parse(this.querySelector('[data-ee-cells]').textContent || '[]');
    this.form = this.querySelector('[data-ee-form]');
    this.canvasWrap = this.querySelector('[data-ee-canvas]');
    this.quoteLink = this.querySelector('[data-ee-quote]');
    this.baseQuoteUrl = this.dataset.quoteUrl || '/pages/contact';

    this.form.addEventListener('input', (e) => this.onInput(e));
    this.form.addEventListener('change', (e) => this.onInput(e));
    this.querySelectorAll('[data-ee-step]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const input = this.form.elements[btn.dataset.eeStep];
        input.value = String(clampToLimit(input, +input.value + +btn.dataset.dir));
        this.update();
      })
    );
    this.querySelector('[data-ee-reset]')?.addEventListener('click', () => this.viewer?.resetView());
    // Layer toggles: cell holders, insulation sheets, BMS
    this.layers = { holders: true, insulation: true, bms: true };
    this.querySelectorAll('[data-ee-layer]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const key = btn.dataset.eeLayer;
        this.layers[key] = !this.layers[key];
        btn.setAttribute('aria-pressed', String(this.layers[key]));
        this.viewer?.setLayers(this.layers);
      })
    );
    this.querySelector('[data-ee-from-reset]')?.addEventListener('click', () => this.startFresh());

    this.defaults = { s: this.form.elements.s.value, p: this.form.elements.p.value };
    this.initDescribe();
    this.applyUrlConfig();
    this.filterCells();
    this.update();

    // Load the 3D engine only when the builder is close to the viewport
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          this.initViewer();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(this);
  }

  /**
   * Pre-load a configuration from the URL, e.g. from a product page:
   * ?from=<product-handle>&cells=18650&s=7&p=12&mah=3500&cell=LG%20MJ1  ("format" is reserved by Shopify, hence "cells")
   * The cell is matched by name, otherwise by the closest capacity within the format.
   */
  applyUrlConfig() {
    const q = new URLSearchParams(window.location.search);
    if (!['cells', 's', 'p', 'cell', 'mah'].some((k) => q.has(k))) return;
    const { elements } = this.form;

    const setFormat = (value) => {
      const radio = [...this.form.querySelectorAll('input[name="format"]')].find((r) => r.value === value);
      if (radio) radio.checked = true;
    };
    if (q.get('cells')) setFormat(q.get('cells'));

    for (const name of ['s', 'p']) {
      const n = parseInt(q.get(name), 10);
      if (n > 0) elements[name].value = String(clampToLimit(elements[name], n));
    }

    const format = elements.format.value;
    const inFormat = this.cells.filter((c) => c.format === format);
    const wanted = (q.get('cell') || '').trim().toLowerCase();
    let pick = wanted ? this.cells.find((c) => c.name.toLowerCase() === wanted) : null;
    const mah = parseFloat(q.get('mah'));
    if (!pick && mah > 0 && inFormat.length) {
      pick = inFormat.reduce((best, c) => (Math.abs(c.capacity - mah) < Math.abs(best.capacity - mah) ? c : best));
    }
    if (pick) {
      setFormat(pick.format);
      const input = [...this.form.querySelectorAll('input[name="cell"]')].find((i) => i.value === pick.id);
      if (input) input.checked = true;
    }

    const handle = q.get('from');
    if (handle) this.showSource(handle);
  }

  async showSource(handle) {
    const banner = this.querySelector('[data-ee-from]');
    const link = this.querySelector('[data-ee-from-link]');
    if (!banner || !link) return;
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}`;
    link.href = url;
    banner.hidden = false;
    this.sourceHandle = handle;
    try {
      const response = await fetch(`${url}.js`);
      const product = response.ok ? await response.json() : null;
      if (product?.title) {
        link.textContent = product.title;
        this.sourceTitle = product.title;
        this.update();
      }
    } catch {
      // keep the generic label
    }
  }

  startFresh() {
    const { elements } = this.form;
    elements.s.value = this.defaults.s;
    elements.p.value = this.defaults.p;
    const firstFormat = this.form.querySelector('input[name="format"]');
    if (firstFormat) firstFormat.checked = true;
    this.form.querySelectorAll('input[name="cell"]').forEach((i) => (i.checked = false));
    this.querySelector('[data-ee-from]').hidden = true;
    this.sourceHandle = null;
    this.sourceTitle = null;
    const clean = new URL(window.location.href);
    clean.search = '';
    window.history.replaceState(null, '', clean);
    this.filterCells();
    this.update();
    this.viewer?.resetView();
  }

  /** "Describe your pack" box: sentence → requirements → solved design applied to the generator */
  initDescribe() {
    const box = this.querySelector('[data-ee-describe]');
    if (!box) return;
    this.describeInput = box.querySelector('[data-ee-describe-input]');
    this.describeResult = box.querySelector('[data-ee-describe-result]');
    this.describeGo = box.querySelector('[data-ee-describe-go]');
    this.describeGo.addEventListener('click', () => this.runDescribe());
    this.describeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.runDescribe();
      }
    });
    box.querySelectorAll('[data-ee-example]').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.describeInput.value = btn.dataset.eeExample;
        this.runDescribe();
      })
    );
    this.describeResult.addEventListener('click', (e) => {
      const alt = e.target.closest('[data-ee-alt]');
      if (alt && this.lastDesign) this.applyDesign(this.lastDesign.choices[+alt.dataset.eeAlt], true);
    });
  }

  async interpret(text) {
    const endpoint = this.dataset.aiEndpoint;
    if (endpoint) {
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12000);
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            cells: this.cells.map(({ name, format, capacity, current }) => ({ name, format, capacity, current })),
            limits: { maxSeries: +this.dataset.maxS, maxParallel: +this.dataset.maxP },
          }),
          signal: ctrl.signal,
        });
        clearTimeout(timer);
        if (res.ok) {
          const data = await res.json();
          if (data?.requirements) return { ...parseRequest(text, this.cells), ...data.requirements };
        }
      } catch {
        // fall back to the built-in parser
      }
    }
    return parseRequest(text, this.cells);
  }

  async runDescribe() {
    const text = this.describeInput.value.trim();
    if (!text) {
      this.describeInput.focus();
      return;
    }
    this.describeGo.setAttribute('aria-busy', 'true');
    const req = await this.interpret(text);
    this.describeGo.removeAttribute('aria-busy');

    const design = solveDesign(req, this.cells, limitOf(this.form.elements.s), limitOf(this.form.elements.p));
    const box = this.describeResult;
    box.hidden = false;
    box.classList.toggle('ee-describe__result--warn', !!design.error);

    if (design.error) {
      const msgs = {
        voltage: 'Please include the voltage you need, e.g. <strong>36V</strong> or <strong>48V</strong>.',
        capacity: 'Add how much energy you need: capacity (<strong>20Ah</strong>), energy (<strong>1kWh</strong>) or runtime and current (<strong>5 hours at 10A</strong>).',
        'too-high': `That voltage needs ${design.S} cells in series, above our ${this.form.elements.s.dataset.limit}S online limit. <a href="#" data-ee-to-quote>Request a custom quote</a> and our engineers will design it.`,
        'too-big': 'No single configuration within our online limits meets this. <a href="#" data-ee-to-quote>Request a custom quote</a> and our engineers will design it.',
      };
      box.innerHTML = `<span>${msgs[design.error]}</span>`;
      box.querySelector('[data-ee-to-quote]')?.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = `${this.baseQuoteUrl}${this.baseQuoteUrl.includes('?') ? '&' : '?'}pack=${encodeURIComponent(`Request: "${text}"`)}`;
      });
      return;
    }

    design.choices = [{ ...design.best, label: 'Suggested' }, ...design.alts];
    this.lastDesign = design;
    this.describeText = text;
    this.applyDesign(design.best, false);
  }

  applyDesign(option, isAlternative) {
    const design = this.lastDesign;
    const { elements } = this.form;
    const { cell, P } = option;
    const radio = [...this.form.querySelectorAll('input[name="format"]')].find((r) => r.value === cell.format);
    if (radio) radio.checked = true;
    this.filterCells();
    const input = [...this.form.querySelectorAll('input[name="cell"]')].find((i) => i.value === cell.id);
    if (input) input.checked = true;
    elements.s.value = String(design.S);
    elements.p.value = String(P);
    this.update();

    const { need } = design;
    const why = [`<li>${design.S} cells in series → ${fmt(design.nominal)} V nominal</li>`];
    if (need.ah) {
      why.push(
        `<li>${P} in parallel × ${fmt(cell.capacity / 1000, 2)} Ah = ${fmt(option.ah)} Ah` +
          ` (needed ${fmt(need.ah)} Ah${need.runtimeNote ? `: ${need.runtimeNote}` : ''})</li>`
      );
    }
    if (need.current) why.push(`<li>Max continuous ${fmt(option.amps, 0)} A, enough for your ${fmt(need.current, 0)} A</li>`);
    why.push(`<li>About ${fmt(option.kg, 1)} kg with ${design.S * P} cells</li>`);

    // Offer every other choice (including going back to the suggestion), never the one on screen
    const alts = design.choices
      .map((alt, i) => (alt.cell === cell ? '' : `<button type="button" class="ee-describe__alt" data-ee-alt="${i}">${alt.label}: ${escapeHtml(alt.cell.name)} ${design.S}S${alt.P}P</button>`))
      .join('');
    this.describeResult.innerHTML =
      `<span class="ee-describe__headline">${isAlternative ? 'Switched to' : 'Suggested'}: ${design.S}S${P}P · ${escapeHtml(cell.name)} (${formatLabel(cell.format)})</span>` +
      `<ul class="ee-describe__why">${why.join('')}</ul>` +
      (alts ? `<div class="ee-describe__alts">${alts}</div>` : '');

    if (window.matchMedia('(max-width: 989px)').matches) {
      this.querySelector('.ee-builder__viewer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onInput(event) {
    const t = event.target;
    // Sliders share one scale; keep each within its own limit (e.g. S ≤ 24 on a 1–30 track)
    if ((t.name === 's' || t.name === 'p') && +t.value !== clampToLimit(t, +t.value)) t.value = String(clampToLimit(t, +t.value));
    if (t.name === 'format') this.filterCells();
    this.update();
  }

  filterCells() {
    const format = this.form.elements.format.value;
    const options = [...this.querySelectorAll('.ee-cell-opt')];
    let firstVisible = null;
    options.forEach((opt) => {
      const match = opt.dataset.format === format;
      opt.hidden = !match;
      if (match && !firstVisible) firstVisible = opt;
    });
    const checked = this.form.querySelector('input[name="cell"]:checked');
    if (!checked || checked.closest('.ee-cell-opt').hidden) {
      const input = firstVisible?.querySelector('input');
      if (input) input.checked = true;
    }
  }

  get state() {
    const cellId = this.form.querySelector('input[name="cell"]:checked')?.value;
    const cell = this.cells.find((c) => c.id === cellId) || this.cells[0];
    return { cell, S: +this.form.elements.s.value, P: +this.form.elements.p.value };
  }

  update() {
    const { cell, S, P } = this.state;
    if (!cell) return;

    for (const name of ['s', 'p']) {
      const input = this.form.elements[name];
      const span = input.max - input.min || 1;
      const limitPos = ((limitOf(input) - input.min) / span) * 100;
      input.style.setProperty('--fill', `${((input.value - input.min) / span) * 100}%`);
      input.style.setProperty('--limit', `${limitPos}%`);
      input.closest('.ee-range')?.style.setProperty('--limit-pos', `${limitPos}%`);
      input.closest('.ee-range')?.style.setProperty('--limit-shift', limitPos < 100 ? '50%' : '0%');
    }

    let length;
    let width;
    let height;
    if (cell.format === 'pouch') {
      ({ length, width, height } = buildPouch(S, P, cell).size);
    } else {
      const dims = CELL_FORMATS[cell.format] || CELL_FORMATS[18650];
      const { layout } = buildCells(S, P, dims.d + GAP);
      length = Math.round(layout.cols * dims.d + (layout.cols - 1) * GAP);
      width = Math.round(layout.rows * dims.d + (layout.rows - 1) * GAP);
      height = Math.round(dims.h + 2);
    }

    const nominal = S * cell.nominal;
    const ah = (P * cell.capacity) / 1000;
    const wh = nominal * ah;
    const amps = P * cell.current;
    const watts = nominal * amps;
    const grams = S * P * cell.weight * OVERHEAD;
    const config = `${S}S${P}P`;

    const out = {
      s: S,
      p: P,
      config,
      'cell-name': `${cell.name} · ${formatLabel(cell.format)}`,
      voltage: `${fmt(nominal)} V`,
      // No cut-off in the cell data (min 0): show the maximum charge voltage only
      range: cell.min > 0 ? `${fmt(S * cell.min)}–${fmt(S * cell.max)} V` : `up to ${fmt(S * cell.max)} V`,
      capacity: `${fmt(ah, ah < 10 ? 2 : 1)} Ah`,
      energy: wh >= 1000 ? `${fmt(wh / 1000, 2)} kWh` : `${fmt(wh, 0)} Wh`,
      current: `${fmt(amps, 0)} A`,
      power: watts >= 1000 ? `${fmt(watts / 1000, 1)} kW` : `${fmt(watts, 0)} W`,
      cells: `${S * P}`,
      weight: grams >= 1000 ? `≈ ${fmt(grams / 1000, 2)} kg` : `≈ ${fmt(grams, 0)} g`,
      size: `${length} × ${width} × ${height}`,
    };
    for (const [key, value] of Object.entries(out)) {
      this.querySelectorAll(`[data-ee-out="${key}"]`).forEach((el) => (el.textContent = value));
    }

    let summary = `${config} · ${cell.name} (${formatLabel(cell.format)}) · ${out.voltage} · ${out.capacity} · ${out.energy} · ${out.current} max`;
    if (this.sourceTitle) summary += ` · based on ${this.sourceTitle}`;
    if (this.describeText) summary += ` · request: "${this.describeText}"`;
    this.currentSummary = summary; // read by assets/ee-intercom.js for "Chat with an engineer"
    const sep = this.baseQuoteUrl.includes('?') ? '&' : '?';
    let href = `${this.baseQuoteUrl}${sep}pack=${encodeURIComponent(summary)}`;
    if (this.sourceHandle) href += `&product=${encodeURIComponent(this.sourceHandle)}`;
    this.quoteLink.href = href;

    this.viewer?.setPack(cell, S, P);
  }

  async initViewer() {
    try {
      const THREE = await import(this.dataset.threeUrl);
      this.viewer = new PackViewer(THREE, this.canvasWrap);
      this.viewer.setLayers(this.layers);
      this.querySelector('[data-ee-loading]')?.remove();
      const { cell, S, P } = this.state;
      this.viewer.setPack(cell, S, P, true);
    } catch (error) {
      console.error('[ee-pack-builder]', error);
      const loading = this.querySelector('[data-ee-loading]');
      if (loading) loading.textContent = '3D preview is not available in this browser. The specs below are still accurate.';
    }
  }
}

class PackViewer {
  constructor(THREE, container) {
    this.THREE = THREE;
    this.container = container;
    this.spherical = { theta: -0.65, phi: 0.95, radius: 400 };
    this.targetRadius = 400;
    this.autoRotate = !reduceMotion;
    this.pointers = new Map();
    this.needsRender = true;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.append(renderer.domElement);
    this.renderer = renderer;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(30, 1, 1, 20000);

    this.scene.environment = this.makeEnvironment();
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xb8c4d8, 0.9));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.bias = -0.0005;
    key.shadow.radius = 6;
    this.scene.add(key, key.target);
    this.key = key;
    const rim = new THREE.DirectionalLight(0xdfe8ff, 0.8);
    rim.position.set(-300, 200, -300);
    this.scene.add(rim);

    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShadowMaterial({ opacity: 0.16 }));
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    this.scene.add(this.ground);

    this.materials = {
      metal: new THREE.MeshStandardMaterial({ color: 0xd9dde3, metalness: 0.85, roughness: 0.28 }),
      insulator: new THREE.MeshStandardMaterial({ color: 0xf4f5f7, roughness: 0.55 }),
      strip: new THREE.MeshStandardMaterial({ color: 0xe6e9ee, metalness: 0.8, roughness: 0.32 }),
      pos: new THREE.MeshStandardMaterial({ color: 0xde544a, roughness: 0.45 }),
      neg: new THREE.MeshStandardMaterial({ color: 0x282828, roughness: 0.5 }),
      holder: new THREE.MeshStandardMaterial({ color: 0x1d2026, roughness: 0.62, metalness: 0.05 }),
      paper: new THREE.MeshStandardMaterial({ color: 0x4f8178, roughness: 0.85, transparent: true, opacity: 0.55, depthWrite: false }),
      paperSolid: new THREE.MeshStandardMaterial({ color: 0x4f8178, roughness: 0.85 }),
      pcb: new THREE.MeshStandardMaterial({ color: 0x1f7a3d, roughness: 0.5, metalness: 0.1 }),
      pcbEdge: new THREE.MeshStandardMaterial({ color: 0xc9c08a, roughness: 0.7 }),
      copper: new THREE.MeshStandardMaterial({ color: 0xc98a4b, metalness: 0.9, roughness: 0.3 }),
      solder: new THREE.MeshStandardMaterial({ color: 0xd8dbe0, metalness: 0.95, roughness: 0.22 }),
      hole: new THREE.MeshStandardMaterial({ color: 0x101214, roughness: 0.8 }),
      heatsink: new THREE.MeshStandardMaterial({ color: 0xb4bcc6, metalness: 0.8, roughness: 0.38 }),
      capacitor: new THREE.MeshStandardMaterial({ color: 0xb39064, roughness: 0.55 }),
      wireRed: new THREE.MeshStandardMaterial({ color: 0xc9352b, roughness: 0.5 }),
      wireBlack: new THREE.MeshStandardMaterial({ color: 0x1b1d21, roughness: 0.5 }),
      chip: new THREE.MeshStandardMaterial({ color: 0x15171a, roughness: 0.45 }),
      plug: new THREE.MeshStandardMaterial({ color: 0xf2f2ee, roughness: 0.6 }),
      pouchSeal: new THREE.MeshStandardMaterial({ color: 0xb8bec7, metalness: 0.55, roughness: 0.35 }),
      tabAl: new THREE.MeshStandardMaterial({ color: 0xe3e6ea, metalness: 0.9, roughness: 0.25 }),
      tabNi: new THREE.MeshStandardMaterial({ color: 0xc4c0b5, metalness: 0.85, roughness: 0.3 }),
      tape: new THREE.MeshStandardMaterial({ color: 0xd9822b, roughness: 0.6 }),
    };
    this.pack = new THREE.Group();
    this.scene.add(this.pack);
    this.layers = { holders: true, insulation: true, bms: true };

    this.bindControls();
    new ResizeObserver(() => this.resize()).observe(container);
    this.resize();

    this.visible = true;
    new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.start();
    }).observe(container);
    this.start();
  }

  start() {
    if (!this.raf) this.raf = requestAnimationFrame(this.loop);
  }

  makeEnvironment() {
    const { THREE } = this;
    const env = new THREE.Scene();
    const room = new THREE.Mesh(
      new THREE.BoxGeometry(10, 10, 10),
      new THREE.MeshBasicMaterial({ color: 0xc9d3e3, side: THREE.BackSide })
    );
    env.add(room);
    const panel = (color, intensity, x, y, z, w, h) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(intensity) }));
      m.position.set(x, y, z);
      m.lookAt(0, 0, 0);
      env.add(m);
    };
    panel(0xffffff, 3, 0, 4.9, 0, 6, 6);
    panel(0xffffff, 1.6, 4.9, 1, 0, 3, 5);
    panel(0xdfe7ff, 1.2, -4.9, 1, 2, 3, 4);
    panel(0x1b3a7b, 1, 0, -2, -4.9, 8, 3);
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const texture = pmrem.fromScene(env, 0.04).texture;
    pmrem.dispose();
    return texture;
  }

  bindControls() {
    const el = this.renderer.domElement;
    const stop = () => {
      this.autoRotate = false;
    };
    el.addEventListener('pointerdown', (e) => {
      stop();
      el.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    });
    el.addEventListener('pointermove', (e) => {
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      if (this.pointers.size === 1) {
        this.spherical.theta -= (e.clientX - prev.x) * 0.008;
        this.spherical.phi = Math.min(1.45, Math.max(0.12, this.spherical.phi - (e.clientY - prev.y) * 0.006));
      } else if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        const before = Math.hypot(a.x - b.x, a.y - b.y);
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const [c, d] = [...this.pointers.values()];
        const after = Math.hypot(c.x - d.x, c.y - d.y);
        if (before > 0) this.zoom(before / after);
      }
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.needsRender = true;
    });
    const up = (e) => this.pointers.delete(e.pointerId);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        stop();
        this.zoom(Math.exp(e.deltaY * 0.001));
      },
      { passive: false }
    );
  }

  zoom(factor) {
    this.targetRadius = Math.min(this.fitRadius * 2.5, Math.max(this.fitRadius * 0.35, this.targetRadius * factor));
    this.needsRender = true;
  }

  resetView() {
    this.spherical.theta = -0.65;
    this.spherical.phi = 0.95;
    this.targetRadius = this.fitRadius;
    this.autoRotate = !reduceMotion;
    this.needsRender = true;
  }

  resize() {
    const { clientWidth: w, clientHeight: h } = this.container;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.bounds) this.fitCamera(false);
    this.needsRender = true;
  }

  /** Show / hide the optional layers without rebuilding the pack */
  setLayers(layers) {
    this.layers = { ...this.layers, ...layers };
    for (const child of this.pack.children) {
      const key = child.userData.layer;
      if (key) child.visible = this.layers[key] !== false;
    }
    this.needsRender = true;
  }

  /** Tag meshes as part of an optional layer */
  layer(key, ...meshes) {
    for (const mesh of meshes) {
      mesh.userData.layer = key;
      mesh.visible = this.layers[key] !== false;
    }
  }

  /** BMS board standing against the pack's long side, on an insulation backing */
  addBms(width, depth, height, centerX = 0, series = 4) {
    const { THREE } = this;
    const M = this.materials;
    const bw = Math.max(26, Math.min(width * 0.78, 110));
    const bh = Math.max(16, Math.min(height * 0.6, 42));
    const z = depth / 2;
    const top = z + 3.4; // front face of the PCB
    const u = Math.min(1, bh / 36); // component scale for small boards
    const parts = [];
    const box = (w, h, d, material, x, y, zz) => {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
      mesh.position.set(centerX + x, y, zz);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.pack.add(mesh);
      parts.push(mesh);
      return mesh;
    };
    const many = (geometry, material, list, shadow = true) => {
      if (!list.length) return;
      parts.push(this.instanced(geometry, material, list.map(([x, ...rest]) => [centerX + x, ...rest]), shadow));
    };
    const cube = () => new THREE.BoxGeometry(1, 1, 1);

    // Board layout (x across, y up, origin at the board centre): heatsink, MOSFET row, then pads / controller / connector
    const hsH = bh * 0.26;
    const hsY = bh / 2 - 1.6 - hsH / 2;
    const mosH = Math.max(3.2, 5.6 * u);
    const mosW = Math.max(3.4, 6 * u);
    const mosY = hsY - hsH / 2 - 1.4 - mosH / 2;
    const lowTop = mosY - mosH / 2 - 1.4;
    const lowBot = -bh / 2 + 2;
    const lowY = (lowTop + lowBot) / 2;
    const lowH = lowTop - lowBot;
    const padW = Math.max(3, 6 * u);
    const padH = Math.min(lowH * 0.8, 6 * u + 1);
    const padXs = [-bw / 2 + 2.5 + padW / 2, -bw / 2 + 2.5 + padW * 1.5 + 1.6];
    const icS = Math.min(lowH * 0.8, Math.max(3.5, 7 * u));
    const icX = padXs[1] + padW / 2 + 3 + icS / 2;
    // Balance connector: one lead per series junction plus the pack negative (S + 1)
    const pitchW = 1.4;
    const conW = Math.min(bw * 0.4, Math.max(8, (series + 1) * pitchW + 2));
    const conH = Math.min(lowH * 0.75, 4.6 * u + 0.8);
    const conD = 3.6 * u + 1;
    const conX = bw / 2 - 2.5 - conW / 2;
    const nMos = Math.max(3, Math.min(10, Math.round((bw - 8) / (mosW + 3.4))));
    const mosXs = Array.from({ length: nMos }, (_, i) => -bw / 2 + 4 + mosW / 2 + i * ((bw - 8 - mosW) / Math.max(1, nMos - 1)));

    // Solder mask with copper pours, traces and silkscreen, drawn onto a texture
    const scale = 512 / bw;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = Math.max(64, Math.round(bh * scale));
    const g = canvas.getContext('2d');
    const px = (x) => (x + bw / 2) * scale;
    const py = (y) => (bh / 2 - y) * scale;
    g.fillStyle = '#0e5a33';
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.fillStyle = '#16703f';
    g.fillRect(px(-bw / 2 + 2.5), py(hsY + hsH / 2 + 0.6), (bw - 5) * scale, (hsH + mosH + 3) * scale);
    g.strokeStyle = '#1c7a49';
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.lineWidth = Math.max(4, 1.6 * scale);
    for (const x of padXs) {
      g.beginPath();
      g.moveTo(px(x), py(lowY));
      g.lineTo(px(x), py(mosY));
      g.stroke();
    }
    g.lineWidth = Math.max(1.5, 0.35 * scale);
    mosXs.forEach((x, i) => {
      const sx = icX + (i / Math.max(1, nMos - 1) - 0.5) * icS * 0.8;
      const ry = lowTop - 0.4 - (i % 3) * 0.35;
      g.beginPath();
      g.moveTo(px(sx), py(lowY + icS / 2));
      g.lineTo(px(sx), py(ry));
      g.lineTo(px(x), py(ry));
      g.lineTo(px(x), py(mosY - mosH / 2));
      g.stroke();
    });
    for (let i = 0; i < 4; i++) {
      const y = lowY - icS * 0.3 + i * icS * 0.2;
      g.beginPath();
      g.moveTo(px(icX + icS / 2), py(y));
      g.lineTo(px(conX - conW / 2 - 1), py(y));
      g.stroke();
    }
    g.strokeStyle = 'rgba(245,245,240,0.9)';
    g.fillStyle = 'rgba(245,245,240,0.92)';
    g.lineWidth = Math.max(1.5, 0.3 * scale);
    g.strokeRect(px(-bw / 2 + 0.9), py(bh / 2 - 0.9), (bw - 1.8) * scale, (bh - 1.8) * scale);
    g.strokeRect(px(icX - icS / 2 - 1.2), py(lowY + icS / 2 + 1.2), (icS + 2.4) * scale, (icS + 2.4) * scale);
    g.strokeRect(px(conX - conW / 2 - 0.8), py(lowY + conH / 2 + 0.8), (conW + 1.6) * scale, (conH + 1.6) * scale);
    g.font = '600 ' + Math.max(9, Math.round(2.2 * u * scale)) + 'px sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'top';
    ['B-', 'P-'].forEach((t, i) => g.fillText(t, px(padXs[i]), py(lowY - padH / 2 - 0.5)));
    g.textBaseline = 'bottom';
    g.fillText('BAL', px(conX), py(lowY + conH / 2 + 1.2));
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;

    box(bw + 8, bh + 8, 0.5, M.paperSolid, 0, 0, z + 1);
    box(bw, bh, 1.6, M.pcbEdge, 0, 0, z + 2.6);
    const mask = new THREE.Mesh(new THREE.PlaneGeometry(bw, bh), new THREE.MeshStandardMaterial({ map: texture, roughness: 0.34, metalness: 0.08 }));
    mask.position.set(centerX, 0, top + 0.02);
    mask.receiveShadow = true;
    mask.userData.ownMaterial = true;
    this.pack.add(mask);
    parts.push(mask);

    // Mounting holes with plated rings
    const disc = () => new THREE.CylinderGeometry(1, 1, 1, 20).rotateX(Math.PI / 2);
    const holes = [-1, 1].flatMap((sx) => [-1, 1].map((sy) => [sx * (bw / 2 - 2.2), sy * (bh / 2 - 2.2)]));
    many(disc(), M.solder, holes.map(([x, y]) => [x, y, top + 0.05, 1.5, 1.5, 0.1]), false);
    many(disc(), M.hole, holes.map(([x, y]) => [x, y, top + 0.1, 0.8, 0.8, 0.12]), false);

    // MOSFETs (DPAK): body, metal tab towards the heatsink, two legs to the power pour
    many(cube(), M.chip, mosXs.map((x) => [x, mosY, top + 0.75, mosW, mosH, 1.5]));
    many(cube(), M.solder, mosXs.map((x) => [x, mosY + mosH / 2 + 0.5, top + 0.3, mosW * 0.8, 1.2, 0.6]));
    many(cube(), M.solder, mosXs.flatMap((x) => [-0.28, 0.28].map((dx) => [x + dx * mosW, mosY - mosH / 2 - 0.6, top + 0.25, 0.5, 1.4, 0.4])));

    // Finned aluminium heatsink over the power stage
    const hsW = bw - 6;
    box(hsW, hsH, 1.4, M.heatsink, 0, hsY, top + 0.7);
    const finD = Math.max(2.5, 5 * u);
    const nFins = Math.max(5, Math.floor(hsW / 2.4));
    many(cube(), M.heatsink, Array.from({ length: nFins }, (_, i) => [-hsW / 2 + 0.4 + i * ((hsW - 0.8) / (nFins - 1)), hsY, top + 1.4 + finD / 2, 0.7, hsH, finD]));

    // Controller IC (QFP) with pins on all four sides
    box(icS, icS, 1.2, M.chip, icX, lowY, top + 0.6);
    const pins = [];
    const nPins = Math.max(4, Math.round(icS / 1.1));
    for (let i = 0; i < nPins; i++) {
      const o = -icS / 2 + 0.6 + i * ((icS - 1.2) / (nPins - 1));
      pins.push([icX + o, lowY + icS / 2 + 0.5, top + 0.2, 0.35, 1, 0.3], [icX + o, lowY - icS / 2 - 0.5, top + 0.2, 0.35, 1, 0.3]);
      pins.push([icX + icS / 2 + 0.5, lowY + o, top + 0.2, 1, 0.35, 0.3], [icX - icS / 2 - 0.5, lowY + o, top + 0.2, 1, 0.35, 0.3]);
    }
    many(cube(), M.solder, pins, false);

    // B- and P- power pads with solder
    many(cube(), M.copper, padXs.map((x) => [x, lowY, top + 0.08, padW, padH, 0.16]), false);
    many(new THREE.SphereGeometry(1, 16, 10), M.solder, padXs.map((x) => [x, lowY, top + 0.2, padW * 0.38, padH * 0.38, 0.9]));

    // Balance connector with its pin row and lead wires down to the cell strips
    box(conW, conH, conD, M.plug, conX, lowY, top + conD / 2);
    const nWires = Math.max(2, Math.min(series + 1, Math.floor((conW - 2) / pitchW) + 1));
    const wireXs = Array.from({ length: nWires }, (_, i) => conX - conW / 2 + 1 + i * ((conW - 2) / (nWires - 1)));
    many(cube(), M.hole, wireXs.map((x) => [x, lowY + conH * 0.12, top + conD + 0.05, 0.7, conH * 0.4, 0.1]), false);
    const wireTop = lowY - conH / 2;
    const wireBot = -height / 2 + 3;
    const wireMid = (wireTop + wireBot) / 2;
    const wireLen = wireTop - wireBot;
    const wireZ = top + 1.4;
    const tube = () => new THREE.CylinderGeometry(1, 1, 1, 10);
    many(tube(), M.wireRed, [[wireXs[nWires - 1], wireMid, wireZ, 0.55, wireLen, 0.55]]);
    many(tube(), M.wireBlack, wireXs.slice(0, -1).map((x) => [x, wireMid, wireZ, 0.55, wireLen, 0.55]));

    // Small passives (resistors and capacitors)
    const smd = [];
    const caps = [];
    const left = icX + icS / 2 + 2.2;
    const right = conX - conW / 2 - 2.2;
    for (let x = left, i = 0; x < right; x += 2.6, i++) {
      for (const dy of [-0.32, 0.32]) {
        const y = lowY + dy * lowH;
        if ((i + (dy > 0 ? 1 : 0)) % 3 === 2) caps.push([x, y, top + 0.45, 1.6, 0.9, 0.9]);
        else smd.push([x, y, top + 0.3, 1.6, 0.8, 0.5]);
      }
    }
    for (let i = 0; i < nMos - 1; i++) smd.push([(mosXs[i] + mosXs[i + 1]) / 2, mosY - mosH * 0.15, top + 0.3, 0.8, 1.6, 0.5]);
    many(cube(), M.chip, smd);
    many(cube(), M.capacitor, caps);
    const ends = smd.flatMap(([x, y, , w, h]) => [-0.7, 0.7].map((e) => (w > h ? [x + e * w * 0.5, y, top + 0.32, w * 0.3, h * 1.02, 0.55] : [x, y + e * h * 0.5, top + 0.32, w * 1.02, h * 0.3, 0.55])));
    many(cube(), M.solder, ends, false);

    this.layer('bms', ...parts);
  }

  clearPack() {
    for (const child of [...this.pack.children]) {
      child.geometry?.dispose();
      if (child.userData.ownMaterial) {
        child.material.map?.dispose();
        child.material.dispose();
      }
      this.pack.remove(child);
    }
  }

  instanced(geometry, material, transforms, shadow = true) {
    const { THREE } = this;
    const mesh = new THREE.InstancedMesh(geometry, material, transforms.length);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    transforms.forEach(([x, y, z, sx = 1, sy = 1, sz = 1], i) => {
      m.compose(new THREE.Vector3(x, y, z), q, new THREE.Vector3(sx, sy, sz));
      mesh.setMatrixAt(i, m);
    });
    mesh.castShadow = shadow;
    mesh.receiveShadow = true;
    this.pack.add(mesh);
    return mesh;
  }

  setPack(cell, S, P, immediate = false) {
    if (cell.format === 'pouch') return this.setPouchPack(cell, S, P, immediate);
    const { THREE } = this;
    const dims = CELL_FORMATS[cell.format] || CELL_FORMATS[18650];
    const r = dims.d / 2;
    const h = dims.h;
    const pitch = dims.d + GAP;
    const { layout, cells } = buildCells(S, P, pitch);

    this.clearPack();

    const seg = S * P > 300 ? 20 : 36;
    const capH = 0.8;
    const wrapMat = new THREE.MeshStandardMaterial({ color: cell.color || '#2f8f4e', roughness: 0.32, metalness: 0.05 });

    const body = [];
    const caps = [];
    const rings = [];
    const nubs = [];
    for (const c of cells) {
      const up = c.g % 2 === 0 ? 1 : -1; // alternate orientation for series connection
      body.push([c.x, 0, c.z]);
      caps.push([c.x, h / 2 - capH / 2, c.z], [c.x, -h / 2 + capH / 2, c.z]);
      rings.push([c.x, up * (h / 2 + 0.15), c.z]);
      nubs.push([c.x, up * (h / 2 + 0.7), c.z]);
    }
    const bodyMesh = this.instanced(new THREE.CylinderGeometry(r, r, h - capH * 2, seg), wrapMat, body);
    bodyMesh.userData.ownMaterial = true;
    this.instanced(new THREE.CylinderGeometry(r * 0.97, r * 0.97, capH, seg), this.materials.metal, caps, false);
    this.instanced(new THREE.CylinderGeometry(r * 0.88, r * 0.88, 0.5, seg), this.materials.insulator, rings, false);
    this.instanced(new THREE.CylinderGeometry(r * 0.34, r * 0.34, 1.2, 16), this.materials.metal, nubs, false);

    // Nickel strips: top joins groups (0,1), (2,3)…; bottom joins (1,2), (3,4)…; free ends are B− and B+
    const stripY = h / 2 + 1.45;
    const strips = [];
    const addStrips = (set, y) => {
      const byRow = new Map();
      const byCol = new Map();
      for (const c of set) {
        (byRow.get(c.row) || byRow.set(c.row, []).get(c.row)).push(c);
        (byCol.get(c.col) || byCol.set(c.col, []).get(c.col)).push(c);
      }
      for (const row of byRow.values()) {
        const xs = row.map((c) => c.x);
        const min = Math.min(...xs);
        const max = Math.max(...xs);
        strips.push([(min + max) / 2, y, row[0].z, max - min + dims.d * 0.55, 0.3, dims.d * 0.42]);
      }
      for (const col of byCol.values()) {
        if (col.length < 2) continue;
        const zs = col.map((c) => c.z);
        const min = Math.min(...zs);
        const max = Math.max(...zs);
        strips.push([col[0].x, y, (min + max) / 2, dims.d * 0.22, 0.3, max - min]);
      }
    };
    const group = (g) => cells.filter((c) => c.g === g);
    for (let g = 0; g < S - 1; g++) addStrips([...group(g), ...group(g + 1)], g % 2 === 0 ? stripY : -stripY);
    addStrips(group(0), -stripY);
    const last = S - 1;
    const lastEndY = last % 2 === 0 ? stripY : -stripY;
    addStrips(group(last), lastEndY);
    this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.strip, strips, false);

    // Terminal tabs pointing to the nearest outside edge
    const halfW = ((layout.cols - 1) / 2) * pitch;
    const halfD = ((layout.rows - 1) / 2) * pitch;
    const tab = (set, y, material) => {
      const opts = set.flatMap((c) => [
        { c, dir: [-1, 0], dist: c.x + halfW },
        { c, dir: [1, 0], dist: halfW - c.x },
        { c, dir: [0, -1], dist: c.z + halfD },
        { c, dir: [0, 1], dist: halfD - c.z },
      ]);
      const { c, dir, dist } = opts.reduce((a, b) => (b.dist < a.dist ? b : a));
      const len = dist + r + 14;
      const w = dims.d * 0.5;
      const cx = c.x + dir[0] * (len / 2);
      const cz = c.z + dir[1] * (len / 2);
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(dir[0] ? len : w, 0.6, dir[1] ? len : w), material);
      mesh.position.set(cx, y + Math.sign(y) * 0.4, cz);
      mesh.castShadow = true;
      this.pack.add(mesh);
    };
    tab(group(0), -stripY, this.materials.neg);
    tab(group(last), lastEndY, this.materials.pos);

    // Cell holders: a plastic bracket section around the top and bottom of every cell
    const holderH = Math.min(9, h * 0.14);
    const holders = [];
    const sheets = [];
    for (const c of cells) {
      holders.push([c.x, h / 2 - holderH / 2 - 2.5, c.z, pitch, holderH, pitch], [c.x, -h / 2 + holderH / 2 + 2.5, c.z, pitch, holderH, pitch]);
      sheets.push([c.x, stripY + 1, c.z, pitch, 0.4, pitch], [c.x, -stripY - 1, c.z, pitch, 0.4, pitch]);
    }
    this.layer('holders', this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.holder, holders));
    // Insulation sheets over the welded strips (semi-transparent so the series links stay visible)
    this.layer('insulation', this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.paper, sheets, false));
    // BMS sits against the cells of the front row (the layout can be L-shaped)
    const frontRow = cells.filter((c) => c.row === layout.rows - 1).map((c) => c.x);
    const frontMin = Math.min(...frontRow) - pitch / 2;
    const frontMax = Math.max(...frontRow) + pitch / 2;
    this.addBms(frontMax - frontMin, layout.rows * pitch, h, (frontMin + frontMax) / 2, S);

    // Frame the pack
    const width = layout.cols * pitch;
    const depth = layout.rows * pitch;
    this.bounds = { width, depth: depth + 14, height: h + 4 };
    this.ground.scale.set(width * 6, depth * 6, 1);
    this.ground.position.y = -h / 2 - 2.2;
    const span = Math.max(width, depth, h);
    this.key.position.set(span * 0.6, span * 1.6, span * 0.9);
    const cam = this.key.shadow.camera;
    cam.left = cam.bottom = -span;
    cam.right = cam.top = span;
    cam.near = 1;
    cam.far = span * 5;
    cam.updateProjectionMatrix();

    this.fitCamera(immediate);
    this.needsRender = true;
  }

  /** Pouch pack: stacked cells standing on edge, tabs on top, busbars linking the series groups */
  setPouchPack(cell, S, P, immediate = false) {
    const { THREE } = this;
    const { t, w, l } = cell;
    const { layout, cells } = buildPouch(S, P, cell);
    this.clearPack();

    const pouchMat = new THREE.MeshStandardMaterial({ color: cell.color || '#c9ced6', metalness: 0.45, roughness: 0.38 });
    const top = l / 2;
    const tabW = Math.max(6, w * 0.16);
    const tabH = 9;
    const tabZ = w / 4;
    const busY = top + 4 + tabH;

    const body = [];
    const seal = [];
    const tape = [];
    const tabsAl = [];
    const tabsNi = [];
    for (const c of cells) {
      body.push([c.x, 0, c.z, t * 0.94, l, w * 0.97]);
      seal.push([c.x, top + 2, c.z, t * 0.35, 4, w * 0.97]);
      tape.push([c.x, top + 4.5, c.z - tabZ, t * 0.6, 1.5, tabW * 1.2], [c.x, top + 4.5, c.z + tabZ, t * 0.6, 1.5, tabW * 1.2]);
      // Alternate polarity per group so + of group g sits next to − of group g+1
      const posSide = c.g % 2 === 0 ? 1 : -1;
      tabsAl.push([c.x, top + 4 + tabH / 2, c.z + posSide * tabZ, 0.6, tabH, tabW]);
      tabsNi.push([c.x, top + 4 + tabH / 2, c.z - posSide * tabZ, 0.6, tabH, tabW]);
    }
    const bodyMesh = this.instanced(new THREE.BoxGeometry(1, 1, 1), pouchMat, body);
    bodyMesh.userData.ownMaterial = true;
    this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.pouchSeal, seal, false);
    this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.tape, tape, false);
    this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.tabAl, tabsAl, false);
    this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.tabNi, tabsNi, false);

    // Busbars: each series link joins group g (+) and g+1 (−) on the same side; free ends are B− and B+
    const group = (g) => cells.filter((c) => c.g === g);
    const sideOf = (g, polarity) => (g % 2 === 0 ? 1 : -1) * (polarity === '+' ? 1 : -1);
    const bars = [];
    const bar = (set, side) => {
      const xs = set.map((c) => c.x);
      const zs = set.map((c) => c.z + side * tabZ);
      const minX = Math.min(...xs) - t / 2;
      const maxX = Math.max(...xs) + t / 2;
      const minZ = Math.min(...zs) - tabW / 2;
      const maxZ = Math.max(...zs) + tabW / 2;
      bars.push([(minX + maxX) / 2, busY + 0.6, (minZ + maxZ) / 2, maxX - minX, 1.2, maxZ - minZ]);
    };
    for (let g = 0; g < S - 1; g++) bar([...group(g), ...group(g + 1)], sideOf(g, '+'));
    bar(group(0), sideOf(0, '-'));
    bar(group(S - 1), sideOf(S - 1, '+'));
    this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.strip, bars, false);

    // Main leads: short cable stubs up from the B− and B+ busbars
    const lead = (set, side, material) => {
      const c = set[Math.floor(set.length / 2)];
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, 22, 16), material);
      mesh.position.set(c.x, busY + 12, c.z + side * tabZ);
      mesh.castShadow = true;
      this.pack.add(mesh);
    };
    lead(group(0), sideOf(0, '-'), this.materials.neg);
    lead(group(S - 1), sideOf(S - 1, '+'), this.materials.pos);

    // Pouch cells use end plates instead of holders, and an insulation sheet over the busbars of every stack
    const plates = [];
    const covers = [];
    for (let r = 0; r < layout.rows; r++) {
      const row = cells.filter((c) => c.row === r);
      if (!row.length) continue;
      const xs = row.map((c) => c.x);
      const minX = Math.min(...xs) - t / 2;
      const maxX = Math.max(...xs) + t / 2;
      const z = row[0].z;
      plates.push([minX - 1.8, 0, z, 2.4, l * 0.98, w], [maxX + 1.8, 0, z, 2.4, l * 0.98, w]);
      covers.push([(minX + maxX) / 2, busY + 2, z, maxX - minX, 0.4, w * 0.98]);
    }
    this.layer('holders', this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.holder, plates));
    this.layer('insulation', this.instanced(new THREE.BoxGeometry(1, 1, 1), this.materials.paper, covers, false));
    const frontStack = cells.filter((c) => c.row === layout.rows - 1).map((c) => c.x);
    const stackMin = Math.min(...frontStack) - t / 2;
    const stackMax = Math.max(...frontStack) + t / 2;
    this.addBms(stackMax - stackMin, layout.rows * (w + POUCH_ROW_GAP) - POUCH_ROW_GAP, l, (stackMin + stackMax) / 2, S);

    // Frame the pack
    const width = layout.cols * (t + POUCH_GAP);
    const depth = layout.rows * (w + POUCH_ROW_GAP);
    const h = l + POUCH_TOP + 22;
    this.bounds = { width: width + 8, depth: depth + 14, height: h };
    this.ground.scale.set(width * 6 + 200, depth * 6 + 200, 1);
    this.ground.position.y = -top - 0.5;
    const span = Math.max(width, depth, h);
    this.key.position.set(span * 0.6, span * 1.6, span * 0.9);
    const cam = this.key.shadow.camera;
    cam.left = cam.bottom = -span;
    cam.right = cam.top = span;
    cam.near = 1;
    cam.far = span * 5;
    cam.updateProjectionMatrix();

    this.fitCamera(immediate);
    this.needsRender = true;
  }

  fitCamera(immediate) {
    const { width, depth, height } = this.bounds;
    const radius = Math.hypot(width / 2, depth / 2, height / 2);
    const fov = (this.camera.fov * Math.PI) / 180;
    const fitH = radius / Math.sin(fov / 2);
    const fitW = fitH / Math.min(1, this.camera.aspect);
    this.fitRadius = Math.max(fitH, fitW) * (this.camera.aspect < 1.1 ? 1.22 : 1.08);
    this.targetRadius = this.fitRadius;
    if (immediate) this.spherical.radius = this.fitRadius;
  }

  loop = () => {
    this.raf = null;
    if (!this.visible) return;
    this.raf = requestAnimationFrame(this.loop);
    const s = this.spherical;
    if (this.autoRotate) {
      s.theta += 0.0025;
      this.needsRender = true;
    }
    if (Math.abs(this.targetRadius - s.radius) > 0.5) {
      s.radius += (this.targetRadius - s.radius) * (reduceMotion ? 1 : 0.14);
      this.needsRender = true;
    }
    if (!this.needsRender) return;
    this.needsRender = false;
    this.camera.position.set(
      s.radius * Math.sin(s.phi) * Math.sin(s.theta),
      s.radius * Math.cos(s.phi),
      s.radius * Math.sin(s.phi) * Math.cos(s.theta)
    );
    this.camera.lookAt(0, 0, 0);
    this.renderer.render(this.scene, this.camera);
  };
}

if (!customElements.get('ee-pack-builder')) customElements.define('ee-pack-builder', EePackBuilder);
